import { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from '../store/toastStore';
import { api } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { OPEN_TICKET_STATUS_PARAM } from '../utils/ticketStatus';
import { Play, Pause, Check, X, ListChecks, FileText, Search, Plus } from 'lucide-react';
import type { Ticket, ActiveTimer, OpenWorkRecordInfo, StartableTask } from '../types';
import { useMediaQuery } from './ui/useOverlay';
import BreakResumeOffer from './BreakResumeOffer';
import PausedResumeOffer from './PausedResumeOffer';
import Dialog from './ui/Dialog';
import Tabs from './ui/Tabs';
import StateBlock from './ui/StateBlock';
import { useStartTimer, type StartTimerTask } from './home/useHomeData';
import { runningWorkTimerFor } from './home/homeModel';
import TimerFlowHost from './timer/TimerFlowHost';
import AttendanceFlowHost from './attendance/AttendanceFlowHost';
import AutoCloseoutNotice from './attendance/AutoCloseoutNotice';
import MonthApprovalGate from './monthApproval/MonthApprovalGate';
import TransientAlert from './ui/TransientAlert';
import { useStopTimer } from './timer/useStopTimer';
import { TimerStartEdit } from './timer/TimerStartEdit';
import { canUseTimer, splitStartableTasks, timerClockStart, timerLoadView, timerRowAction } from './timer/timerModel';
import OpenRecordRow from './timer/OpenRecordRow';
import { startablePathText } from '../utils/directUnderOte';
import { TONE_DOT, WORK_STATUS_TONE } from './ui/tone';
import { normalizeMilestoneStatus } from './projects/milestoneStatusModel';
import EngagementFilterFields, { useEngagementFilter } from './engagements/EngagementFilterFields';
import { taskContainerLabel } from '../utils/engagementLabel';
import TimerSummaryModal, {
  apiErrorMessage,
  timerTargetFromOpenRecord,
  timerTargetFromTimer,
  type TimerTarget,
} from './TimerSummaryModal';
import EntityPicker from './ui/EntityPicker';
import { useInternalOrgId } from '../hooks/useInternalOrg';
import { usePermissions } from '../hooks/usePermissions';
import { CUSTOMERS_GROUP_KEY, INTERNAL_WORK_LABEL_KEY, internalFirstPickerOptions, withCompanyListed } from '../utils/internalWorkOption';

function formatElapsedAt(startedAt: string, now: number): string {
  const diff = Math.max(0, now - new Date(startedAt).getTime());
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

type TabType = 'tasks' | 'tickets';

/** One task row of the "התחל עבודה" window: ▶ title — customer › project › milestone, status dot. */
export function StartableRow({ task, busy, onStart, running = null, onPause }: { task: StartableTask; busy: boolean; onStart: (task: StartableTask) => void; running?: ActiveTimer | null; onPause?: (timer: ActiveTimer) => void }) {
  const { t } = useTranslation();
  const path = startablePathText(task, { company: t('engagementChain.internalWork'), direct: t('engagementChain.directlyUnderOte') });
  const status = normalizeMilestoneStatus(task.status);
  return (
    <li>
      <button
        type="button"
        disabled={busy}
        onClick={() => (running && onPause ? onPause(running) : onStart(task))}
        data-running={running ? 'true' : undefined}
        className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-start text-sm text-text transition-colors duration-micro hover:bg-surface-subtle disabled:cursor-wait"
      >
        {/* D-332: a task whose timer runs shows pause, not start. */}
        {running ? <Pause size={14} strokeWidth={1.75} className="shrink-0 text-accent-text" aria-hidden="true" /> : <Play size={14} strokeWidth={1.75} className="shrink-0 text-accent-text" aria-hidden="true" />}
        <span className="min-w-0 flex-1">
          <span className="block truncate font-medium"><bdi>{task.title}</bdi></span>
          {path && <span className="block truncate text-xs text-text-2"><bdi>{path}</bdi></span>}
        </span>
        <span
          className={`h-2.5 w-2.5 shrink-0 rounded-full ${TONE_DOT[WORK_STATUS_TONE[status]]}`}
          title={t(`milestoneStatus.${status}`)}
        />
        <span className="sr-only">{t(`milestoneStatus.${status}`)}</span>
      </button>
    </li>
  );
}

/**
 * P-10a (D-67) — "התחל עבודה": the ONE task picker of the timer.
 *
 *   🔍 חיפוש משימה...          לקוח [כל הלקוחות שלי ▾]
 *   אחרונות                    — the tasks I worked on lately
 *   כל המשימות שלי (פתוחות)     — the rest
 *
 * ONLY tasks I am on (accountable or assigned) that are not completed and
 * not cancelled (GET /timer/tasks); a customer narrows them to that customer.
 * It replaces the chain picker (customer → engagement → milestone) the
 * floating button had. Service tickets stay as they were (D-47): their tab is
 * unchanged.
 */
function StartTimerModal({ onClose, onStart, busy, timers = [], onPause }: {
  onClose: () => void;
  onStart: (task: StartTimerTask) => void;
  busy: boolean;
  timers?: ActiveTimer[];
  onPause?: (timer: ActiveTimer) => void;
}) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<TabType>('tasks');
  const [search, setSearch] = useState('');
  const [customer, setCustomer] = useState('');
  const user = useAuthStore(s => s.user);
  const isAdmin = user?.role === 'admin';
  // D-274 §1 — "עבודה פנימית OTE" first in the customer filter.
  const internalOrgId = useInternalOrgId();
  // D-275 — an internal-only employee: her tasks only (no "קריאות" tab, no customer filter).
  const { internalOnly } = usePermissions();

  // My open tasks (D-67): one request; the customer and the search narrow it here.
  const { data: mineData, isLoading: mineLoading, isError: mineError } = useQuery({
    queryKey: ['timer-startable-tasks'],
    queryFn: () => api.getStartableTasks(),
    enabled: activeTab === 'tasks',
    staleTime: 0,
    retry: 1,
  });
  const view = useMemo(
    () => splitStartableTasks(mineData?.data ?? [], { organizationId: customer || null, search }),
    [mineData, customer, search]
  );

  // Service tickets — as before (D-47): the chain filter and the open tickets.
  const filter = useEngagementFilter(activeTab === 'tickets');
  const selectedOrgId = filter.value.organizationId;
  const filterKey = JSON.stringify(filter.value);
  const { data: ticketsData, isLoading: ticketsLoading, isError: ticketsError } = useQuery({
    queryKey: ['tickets', 'timer-tickets', filterKey, search],
    queryFn: () => api.getTickets({
      limit: 100,
      // Every open status, not only "בתכנון" (owner 2026-10-11).
      status: OPEN_TICKET_STATUS_PARAM,
      task_type: 'ticket',
      search: search || undefined,
      organization_id: selectedOrgId || undefined,
      engagement_type: filter.ticketParams.engagement_type,
      engagement_id: filter.ticketParams.engagement_id,
    }),
    enabled: activeTab === 'tickets',
    retry: 1,
  });
  const tickets = (ticketsData?.data ?? []) as Ticket[];

  const start = (task: { id: string; title: string }) => onStart({ id: task.id, title: task.title });
  // D-275 (Q2) — her internal discussions (and anyone's who picked "עבודה פנימית OTE"), a small group under the tasks.
  const showInternalDiscussions = internalOnly || (!!internalOrgId && customer === internalOrgId);
  const { data: discData } = useQuery({
    queryKey: ['timer-internal-discussions', search],
    queryFn: () => api.getInternalDiscussions({ search: search || undefined }),
    enabled: activeTab === 'tasks' && showInternalDiscussions,
    staleTime: 0,
    retry: 1,
  });
  const discussions = showInternalDiscussions ? (discData?.data ?? []) : [];
  const empty = view.recent.length === 0 && view.rest.length === 0 && discussions.length === 0;

  return (
    <Dialog open onClose={onClose} title={t('timer.startWork')} size="md">
        {!internalOnly && <Tabs
          variant="segmented"
          ariaLabel={t('timer.startWork')}
          value={activeTab}
          onChange={(k) => { setActiveTab(k); setSearch(''); }}
          className="mb-4 w-full [&>button]:flex-1 [&>button]:justify-center"
          items={[
            { key: 'tasks', label: t('timer.tabTasks'), icon: <ListChecks size={14} strokeWidth={1.75} aria-hidden="true" /> },
            { key: 'tickets', label: t('timer.tabTickets'), icon: <FileText size={14} strokeWidth={1.75} aria-hidden="true" /> },
          ]}
        />}

        {activeTab === 'tasks' && (
          <div aria-busy={mineLoading || undefined}>
            <div className="mb-3 flex flex-col gap-2 sm:flex-row">
              <div className="relative flex-1">
                <Search size={14} className="absolute start-3 top-1/2 -translate-y-1/2 text-text-3" aria-hidden="true" />
                <input
                  type="search"
                  placeholder={t('timer.searchTaskPlaceholder')}
                  aria-label={t('timer.searchTaskPlaceholder')}
                  className="input text-sm w-full ps-9"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              {!internalOnly && <div className="flex items-center gap-2 text-sm text-text-2">
                <span className="shrink-0" aria-hidden="true">{t('timer.customer')}</span>
                <EntityPicker
                  aria-label={t('timer.customer')}
                  className="min-w-44"
                  emptyLabel={t('timer.allMyCustomers')}
                  options={internalFirstPickerOptions(withCompanyListed(view.customers, internalOrgId), internalOrgId, t(INTERNAL_WORK_LABEL_KEY), t(CUSTOMERS_GROUP_KEY))}
                  value={customer}
                  onChange={setCustomer}
                />
              </div>}
            </div>
            {mineLoading ? (
              <StateBlock kind="loading" size="inline" />
            ) : mineError ? (
              <p className="py-4 text-center text-sm text-danger-700">{t('timer.loadError')}</p>
            ) : empty ? (
              <p className="py-4 text-center text-sm text-text-2">{t('timer.noMyTasks')}</p>
            ) : (
              <div className="max-h-80 space-y-3 overflow-y-auto">
                {view.recent.length > 0 && (
                  <section aria-labelledby="timer-recent-title">
                    <h3 id="timer-recent-title" className="mb-1 px-3 text-xs font-semibold text-text-2">{t('timer.recentTitle')}</h3>
                    <ul className="space-y-0.5">
                      {view.recent.map((task) => <StartableRow key={task.id} task={task} busy={busy} onStart={start} running={runningWorkTimerFor(timers, task.id)} onPause={onPause} />)}
                    </ul>
                  </section>
                )}
                {view.rest.length > 0 && (
                  <section aria-labelledby="timer-all-title">
                    <h3 id="timer-all-title" className="mb-1 px-3 text-xs font-semibold text-text-2">{t('timer.allMyOpenTitle')}</h3>
                    <ul className="space-y-0.5">
                      {view.rest.map((task) => <StartableRow key={task.id} task={task} busy={busy} onStart={start} running={runningWorkTimerFor(timers, task.id)} onPause={onPause} />)}
                    </ul>
                  </section>
                )}
                {discussions.length > 0 && (
                  <section aria-labelledby="timer-discussions-title">
                    <h3 id="timer-discussions-title" className="mb-1 px-3 text-xs font-semibold text-text-2">{t('timer.internalDiscussionsTitle')}</h3>
                    <ul className="space-y-0.5">
                      {discussions.map((d) => <StartableRow key={d.id} task={d} busy={busy} onStart={start} running={runningWorkTimerFor(timers, d.id)} onPause={onPause} />)}
                    </ul>
                  </section>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'tickets' && (<>
        <div className="space-y-2 mb-3">
          <EngagementFilterFields filter={filter} idPrefix="timer" allowAllCustomers={isAdmin} compact />
          <div className="relative">
            <Search size={14} className="absolute start-3 top-1/2 -translate-y-1/2 text-surface-500" />
            <input
              type="text"
              placeholder={t('timer.searchTicket')}
              className="input text-sm w-full ps-9"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        <div className="max-h-60 overflow-y-auto space-y-1">
          {ticketsLoading ? (
            <p className="text-sm text-surface-500 text-center py-4">...</p>
          ) : ticketsError ? (
            <p className="text-sm text-danger-700 text-center py-4">{t('timer.loadError')}</p>
          ) : tickets.length === 0 ? (
            <p className="text-sm text-surface-500 text-center py-4">{t('timer.noTicketsFound')}</p>
          ) : (
            tickets.map(ticket => (
              <button
                key={ticket.id}
                type="button"
                disabled={busy}
                onClick={() => start(ticket)}
                className="w-full text-start px-3 py-2 rounded-lg hover:bg-primary-50 transition-colors text-sm text-surface-700 flex items-center gap-2"
              >
                <Play size={14} className="text-accent-text flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <span className="truncate block font-medium">{ticket.title}</span>
                  <span className="text-xs text-surface-500 truncate block">
                    {ticket.organization_name}
                    {taskContainerLabel(ticket, t) && ` · ${taskContainerLabel(ticket, t)}`}
                  </span>
                </div>
              </button>
            ))
          )}
        </div>
        </>)}
    </Dialog>
  );
}

export default function GlobalTimer() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  // D-347 §3: the capsule shows on Home too (one timer, always in the same place);
  // Home's header no longer carries its own live timer.
  // FQ-039: on a phone page with a docked composer (ticket page) the capsule lives inside the
  // dock (a row above the input) instead of floating over the conversation.
  const isPhone = useMediaQuery('(max-width: 767px)');
  const [dockSlot, setDockSlot] = useState<HTMLElement | null>(null);
  useEffect(() => {
    if (!isPhone) { setDockSlot(null); return undefined; }
    const find = () => setDockSlot((cur) => {
      const el = document.querySelector<HTMLElement>('[data-timer-slot]');
      return el === cur ? cur : el;
    });
    find();
    const mo = new MutationObserver(find);
    mo.observe(document.body, { childList: true, subtree: true });
    return () => mo.disconnect();
  }, [isPhone]);
  const [showStartModal, setShowStartModal] = useState(false);
  const [summaryTarget, setSummaryTarget] = useState<TimerTarget | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  // FIX-OPENREC — by OTE-employee identity (as the server's requireOteStaff), not the role claim.
  const { isOteEmployee } = usePermissions();
  const isAgentOrAdmin = !!user && canUseTimer(user.role, isOteEmployee);

  const { data: timerData, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['timer-status'],
    queryFn: () => api.getTimerStatus(),
    enabled: !!isAgentOrAdmin,
    refetchInterval: 30000,
    staleTime: 0, // Never serve stale timer data — each user's timers are independent
  });

  // FIX-OPENREC — a failed load (error / timeout) keeps the capsule with a retry, never a silent nothing.
  const loadView = timerLoadView({ isLoading, isError, hasData: !!timerData });
  const loadFailed = loadView === 'error';
  const timers: ActiveTimer[] = timerData?.data?.timers ?? [];
  const openRecords: OpenWorkRecordInfo[] = timerData?.data?.open_records ?? [];
  const running = timers.filter((tm) => tm.status === 'running');

  useEffect(() => {
    if (running.length === 0) return;
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [running.length]);

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['timer-status'] });
    queryClient.invalidateQueries({ queryKey: ['work-logs'] });
    queryClient.invalidateQueries({ queryKey: ['work-records'] });
  }, [queryClient]);

  // Command palette quick action (u5): window event opens the start dialog.
  useEffect(() => {
    const open = () => setShowStartModal(true);
    window.addEventListener('ote:start-timer', open);
    return () => window.removeEventListener('ote:start-timer', open);
  }, []);

  // Home's "N רשומות פתוחות" opens this panel.
  useEffect(() => {
    const open = () => setExpanded(true);
    window.addEventListener('ote:timers-panel', open);
    return () => window.removeEventListener('ote:timers-panel', open);
  }, []);
  const closePanel = () => setExpanded(false);
  const showPanel = expanded;

  // The shared start (asks before running next to another task's timer).
  const startMutation = useStartTimer({
    silentErrors: true,
    onStarted: () => {
      setShowStartModal(false);
      setExpanded(true);
    },
  });

  const handleSummaryDone = useCallback(() => {
    setSummaryTarget(null);
    invalidate();
  }, [invalidate]);

  // D-64 — ⏸ stops at once (no window); ✓ opens the finish window.
  const stopMutation = useStopTimer();
  // P-37 (D-194 Q-173) — "אני על זה" ends with "גמרתי": no window, no "מה בוצע".
  const replyDone = useMutation({
    mutationFn: (tm: ActiveTimer) => api.finishReplyTimer(tm.ticket_id),
    onSuccess: (res) => {
      const d = res?.data;
      if (d?.logged) toast('success', t('replyTimer.doneLogged', { n: Math.round(Number(d.duration_minutes) || 0) }));
      else toast('info', t('replyTimer.doneUnderMinute'));
    },
    onError: (err) => toast('error', apiErrorMessage(err) || t('replyTimer.doneFailed')),
    onSettled: () => invalidate(),
  });

  // D-252 — "להריץ את שתיהן": the task timers this "אני על זה" paused run again now; it keeps running.
  const runBothMutation = useMutation({
    mutationFn: (replyTimerId: string) => api.resumePausedTimers(replyTimerId),
    onSuccess: () => toast('success', t('replyTimer.bothRunning')),
    onError: () => toast('error', t('replyTimer.bothFailed')),
    onSettled: () => invalidate(),
  });

  if (!isAgentOrAdmin) return null;
  // The timer-switch / close-after-stop host stays mounted at the same place
  // while the status loads, so a question asked early is not lost.
  if (loadView === 'loading') return <><TimerFlowHost /><AttendanceFlowHost /><MonthApprovalGate /></>;

  const first = running[0] ?? timers[0];

  const wrapDock = (el: JSX.Element) => (dockSlot ? createPortal(el, dockSlot) : el);

  return (
    <>
      <TimerFlowHost />
      {/* P-10e (D-85) — "הפסקה" / "החתמת יציאה" from every place: one window. */}
      <AttendanceFlowHost />
      {/* P-37 (D-194 Q-185) — after the clock-in: "לא החתמת יציאה אתמול…" (once). */}
      <AutoCloseoutNotice />
      {/* D-319 s.1 - the month that ended must be approved: a window that cannot be closed. */}
      <MonthApprovalGate />
      {/* Timer capsule (M3, PRD §4.9): only while a timer runs or records are
          open; never on Home. It sits on the QuickCreate button's row, beside it
          (`.timer-dock` in index.css), so the two never overlap. The idle "Start
          work" button is gone (principle 0): start a timer from QuickCreate, the
          command palette, Home or any ticket/task. */}
      {wrapDock(
      <div
        className={dockSlot
          ? 'flex flex-col items-stretch px-2 pb-2'
          : 'timer-dock fixed z-nav flex flex-col items-end pointer-events-none [&>*]:pointer-events-auto'}
      >
        {startMutation.isError && (
          // D-285 — a light error: leaves by itself after ~4 s, ✕ closes it (it used to stay until the next start).
          <TransientAlert
            message={apiErrorMessage(startMutation.error) || t('timer.startError')}
            onClose={() => startMutation.reset()}
            className="mb-2 bg-danger-50 border border-danger-100 text-danger-700 px-3 py-2 rounded-lg text-xs shadow-elevated max-w-xs"
          />
        )}

        {showPanel && (timers.length > 0 || openRecords.length > 0) && (
          <div className="mat-float mb-2 w-full md:w-80 rounded-xl p-3 space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-text">
                {t('timeTasks.timers.panelTitle', { count: timers.length })}
              </p>
              <button type="button" onClick={closePanel} aria-label={t('shell.close')} className="btn-icon -m-1.5 text-text-2 hover:text-text">
                <X size={16} aria-hidden="true" />
              </button>
            </div>
            {timers.map((tm) => (
              <div key={tm.id} className="flex items-center gap-2 rounded-lg bg-surface/70 px-2 py-1.5">
                <div className={tm.status === 'running' ? 'live-dot' : 'status-dot bg-warning-600'} />
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-text truncate">{tm.ticket_title}</p>
                  <p className="text-xs text-text-2 tabular-nums">
                    {tm.status === 'running'
                      ? <>{formatElapsedAt(timerClockStart(tm), now)}{tm.kind === 'reply' && <> · {t('replyTimer.capsuleLabel')}</>}</>
                      : tm.paused_by_timer_id && tm.paused_by_timer_id !== tm.id ? t('replyTimer.pausedShort') : t('timeTasks.timers.suspendedShort')}
                  </p>
                  <TimerStartEdit timerId={tm.id} startedAt={String(tm.started_at)} className="text-xs text-text-2" />
                  {/* D-252 — the pop-up too: a running "אני על זה" that paused a task offers "להריץ את שתיהן". */}
                  {tm.kind === 'reply' && tm.status === 'running' && timers.some((x) => x.status === 'suspended' && x.paused_by_timer_id === tm.id) && (
                    <button
                      type="button"
                      onClick={() => runBothMutation.mutate(tm.id)}
                      disabled={runBothMutation.isPending}
                      className="text-xs font-medium text-accent-text hover:underline underline-offset-2"
                      data-testid="timer-run-both"
                    >
                      {t('replyTimer.runBoth')}
                    </button>
                  )}
                </div>
                {tm.kind === 'reply' ? (
                  <button
                    type="button"
                    onClick={() => replyDone.mutate(tm)}
                    disabled={replyDone.isPending}
                    className="rounded-full bg-success-50 px-2.5 py-1 text-xs font-medium text-success-700 hover:bg-success-100 transition-colors"
                    aria-label={t('replyTimer.doneFor', { title: tm.ticket_title ?? '' })}
                  >
                    {t('replyTimer.done')}
                  </button>
                ) : (<>
                {/* P-10a (D-63/D-64): each timer its own ⏸ (stops at once) and ✓ (finish window). */}
                {/* HN-3: the button shows the REAL state - a running timer offers ⏸, a stopped one (break, "עצירה ומעבר") offers ▶ (it used to be a dead ⏸). */}
                {timerRowAction(tm.status) === 'pause' ? (
                  <button
                    type="button"
                    onClick={() => stopMutation.mutate(tm)}
                    disabled={stopMutation.isPending}
                    className="btn-icon rounded-full bg-surface-subtle hover:bg-surface-muted transition-colors"
                    title={t('timer.pause')}
                    aria-label={t('timer.pauseFor', { title: tm.ticket_title ?? '' })}
                    data-timer-action="pause"
                  >
                    <Pause size={12} className="text-text" aria-hidden="true" />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => startMutation.mutate({ id: tm.ticket_id, title: tm.ticket_title ?? '' })}
                    disabled={startMutation.isPending}
                    className="btn-icon rounded-full bg-accent-soft hover:bg-surface-muted transition-colors"
                    title={t('timer.resume')}
                    aria-label={t('timer.resumeFor', { title: tm.ticket_title ?? '' })}
                    data-timer-action="resume"
                  >
                    <Play size={12} className="text-accent-text" aria-hidden="true" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setSummaryTarget(timerTargetFromTimer(tm))}
                  className="btn-icon rounded-full bg-success-50 hover:bg-success-100 transition-colors"
                  title={t('timer.finish')}
                  aria-label={t('timer.finishFor', { title: tm.ticket_title ?? '' })}
                >
                  <Check size={12} className="text-success-700" aria-hidden="true" />
                </button>
                </>)}
              </div>
            ))}
            {openRecords.length > 0 && (
              <div className="pt-1 border-t border-border">
                <p className="text-xs text-text-2 mb-1">{t('timeTasks.timers.openRecords')}</p>
                {openRecords.slice(0, 5).map((rec) => (
                  <OpenRecordRow
                    key={rec.work_record_id}
                    rec={rec}
                    busy={startMutation.isPending}
                    onResume={(r) => startMutation.mutate({ id: r.ticket_id, title: r.ticket_title ?? '' })}
                    onFinish={(r) => setSummaryTarget(timerTargetFromOpenRecord(r))}
                  />
                ))}
              </div>
            )}
            <button
              type="button"
              onClick={() => setShowStartModal(true)}
              className="w-full flex items-center justify-center gap-1.5 text-xs font-medium text-accent-text hover:bg-accent-soft rounded-lg py-1.5"
            >
              <Plus size={14} aria-hidden="true" />
              {t('timeTasks.timers.startAnother')}
            </button>
          </div>
        )}

        {loadFailed && (
          <button
            type="button"
            onClick={() => { void refetch(); }}
            disabled={isFetching}
            className="mat-float rounded-full px-3 h-touch text-xs font-medium text-danger-700"
            data-testid="timer-load-error"
          >
            {t('timeTasks.timers.loadError')}
          </button>
        )}

        {first && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="mat-float flex h-12 md:h-touch max-w-full items-center gap-2.5 rounded-full px-4 transition-transform duration-press active:scale-[0.98]"
            aria-expanded={expanded}
          >
            <div className={running.length > 0 ? 'live-dot' : 'status-dot bg-warning-600'} />
            <span className="text-sm font-semibold tabular-nums text-text" dir="ltr">
              {first.status === 'running' ? formatElapsedAt(timerClockStart(first), now) : t('timeTasks.timers.suspendedShort')}
            </span>
            <span className="min-w-0 max-w-48 truncate text-xs text-text-2">{first.ticket_title}</span>
            {timers.length > 1 && (
              <span className="text-xs font-semibold bg-accent-soft text-accent-soft-text rounded-full px-2 py-0.5">
                {t('timeTasks.timers.moreCount', { count: timers.length - 1 })}
              </span>
            )}
          </button>
        )}

        {!first && openRecords.length > 0 && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            className="mat-float rounded-full px-3 h-touch text-xs font-medium text-text-2"
          >
            {t('timeTasks.timers.openRecordsCount', { count: openRecords.length })}
          </button>
        )}
      </div>
      )}

      {showStartModal && (
        <StartTimerModal
          onClose={() => setShowStartModal(false)}
          onStart={(task) => startMutation.mutate(task)}
          busy={startMutation.isPending}
          timers={timers}
          onPause={(tm) => stopMutation.mutate(tm)}
        />
      )}

      {summaryTarget && (
        <TimerSummaryModal
          target={summaryTarget}
          onClose={() => setSummaryTarget(null)}
          onDone={handleSummaryDone}
        />
      )}

      <BreakResumeOffer />
      {/* FIX-B17 (D-198 QX-147) — the offer to continue a task left paused by "אני על זה". */}
      <PausedResumeOffer />

    </>
  );
}
